import { useCallback, useState } from "react";
import { FileError, FileRejection, useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FiFile, FiX } from "react-icons/fi";

export interface UploadableFile {
  file: File;
  errors: FileError[];
}
function bytesToMegabytes(bytes: number): number {
  return bytes / (1024 * 1024);
}
function truncateFileName(fileName: string, maxLength: number): string {
  if (fileName.length > maxLength) {
    const truncatedName = fileName.substring(0, maxLength);
    return truncatedName;
  }
  return fileName;
}

function UploadCommercialRegisterFileComponent() {
  const { t } = useTranslation();
  const [files, setFiles] = useState<UploadableFile[]>([]);

  const onDrop = useCallback(
    (acceptedFile: File[], rejectedFile: FileRejection[]) => {
      const acceptedFileMapped = acceptedFile.map((file) => ({
        file,
        errors: [],
      }));
      setFiles((current) => [
        ...current,
        ...acceptedFileMapped,
        ...rejectedFile,
      ]);
    },
    []
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
    },
  });
  const clearFiles = () => {
    setFiles([]);
  };
  return (
    <div className="flex flex-col gap-2">
      <label className="text-zinc700 text-xl leading-[30px] font-arMedium">
        {t("form.uploadCommercialRegisterFile.component.title")}
      </label>
      <div
        className="w-[425px] h-24 bg-transparent mobile:max-w-[345px] mobile:w-screen mobile:h-[73px] rounded-[18px] border border-zinc400 cursor-pointer"
        {...(files.length === 0 && getRootProps())}
      >
        {files.length !== 0 ? (
          <div className="flex flex-col items-center justify-center h-24 mobile:h-[73px]">
            {" "}
            <div className="inline-flex p-2 gap-6 bg-zinc-100 rounded items-center justify-center">
              <div className="flex flex-row gap-2.5">
                <p className="text-zinc400 text-xs font-arMedium leading-[27px]">
                  {bytesToMegabytes(files[0].file.size).toFixed(2)} M
                </p>
                <div className="w-[121px] h-5 relative">
                  <div className="w-[100px] h-1.5 left-0 top-[7px] absolute bg-red-400 rounded" />
                </div>
                <FiX
                  size={18}
                  onClick={clearFiles}
                  className="text-zinc400 cursor-pointer"
                />
              </div>
              <div className="flex flex-row gap-2.5">
                <p className="text-zinc500 text-xs font-arMedium leading-[27px] mobile:hidden">
                  {truncateFileName(files[0].file.name, 15)}
                </p>
                <p className="text-zinc500 text-xs font-arMedium leading-[27px] laptop:hidden largeLaptop:hidden desktop:hidden tablet:hidden largeDesktop:hidden">
                  {truncateFileName(files[0].file.name, 10)}
                </p>
                <FiFile size={24} className="text-zinc500" />
              </div>
            </div>
          </div>
        ) : (
          <div>
            {isDragActive ? (
              <div className="flex flex-col items-center justify-center h-24 mobile:h-[73px]">
                <p className="text-lg mobile:text-xs font-arMedium text-zinc-300">
                  {t(
                    "form.uploadCommercialRegisterFile.component.dropFileHere"
                  )}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-24 mobile:h-[73px]">
                <div className="flex flex-row gap-1 justify-center items-center text-lg mobile:text-xs font-arMedium text-zinc-300">
                  <p>
                    {t(
                      "form.uploadCommercialRegisterFile.component.dragAndDropFile"
                    )}
                  </p>
                  <p className="text-mint-500">
                    {t(
                      "form.uploadCommercialRegisterFile.component.attachFile"
                    )}
                  </p>
                </div>
                <div className="flex justify-center items-center text-xs mobile:text-xs font-arMedium text-zinc-300">
                  {t(
                    "form.uploadCommercialRegisterFile.component.fileSizeLimit"
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <input {...getInputProps()} />
      </div>
    </div>
  );
}

export default UploadCommercialRegisterFileComponent;